import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { formatCurrency } from '../utils/currency';
import './VendorDetail.css';
import { toast } from '../components/Toast';

function VendorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [allTransactions, setAllTransactions] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [payments, setPayments] = useState([]);
  const [returns, setReturns] = useState([]);
  const [outstandingAmount, setOutstandingAmount] = useState(0);
  const [activeTab, setActiveTab] = useState('purchases');
  const [loading, setLoading] = useState(false);

  const [selectedItem, setSelectedItem] = useState(null);
  const [modalType, setModalType] = useState(''); // 'expense', 'payment', 'return'

  useEffect(() => {
    fetchVendorDetails();
    fetchTransactions();
  }, [id]);

  const fetchVendorDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/vendors/${id}`);
      const data = response.data.data || response.data;
      setVendor(data.vendor || data);
      setOutstandingAmount(data.outstandingAmount || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch vendor details');
    } finally {
      setLoading(false);
    }
  };

  const convertToModalFormat = (txn) => {
    return {
      id: txn.id,
      voucherNo: txn.voucherNo,
      amount: txn.amount,
      expenseDate: txn.transactionDate,
      paymentDate: txn.transactionDate,
      projectName: txn.projectName || '-',
      category: txn.accountHeadName || 'General',
      paymentMethod: txn.paymentMode || 'CASH',
      transactionRef: txn.paymentReferenceNo || txn.referenceNo || '-',
      description: txn.notes || '',
      notes: txn.notes || '',
      items: txn.items || [],
      transactionType: txn.transactionType
    };
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`/financial-transactions/vendor/${id}`);
      const list = response.data.data || response.data || [];
      const mapped = list.map(convertToModalFormat);
      
      setAllTransactions(mapped);
      
      const purList = mapped.filter(t => t.transactionType === 'PURCHASE' || t.transactionType === 'EXPENSE');
      const payList = mapped.filter(t => t.transactionType === 'PAYMENT' || t.transactionType === 'ADVANCE' || t.transactionType === 'INCOME');
      const retList = mapped.filter(t => t.transactionType === 'PURCHASE_RETURN');
      
      setPurchases(purList);
      setPayments(payList);
      setReturns(retList);
    } catch (err) {
      console.error('Failed to fetch vendor transactions:', err);
    }
  };









  if (loading) {
    return <div className="loading">Loading vendor details...</div>;
  }

  if (!vendor) {
    return <div className="error">Vendor not found</div>;
  }

  const assocRefund = selectedItem && selectedItem.transactionType === 'PURCHASE_RETURN'
    ? allTransactions.find(t => t.notes && t.notes.includes(`Paid against Purchase Voucher: ${selectedItem.voucherNo}`))
    : null;

  return (
    <div className="vendor-detail-container">
      <div className="page-header">
        <div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/vendors')}>
            ← Back to Vendors
          </button>
          <h1>Vendor Details</h1>
        </div>
      </div>

      <div className="vendor-info-card">
        <div className="info-grid">
          <div className="info-item">
            <label>Name:</label>
            <span>{vendor.name}</span>
          </div>
          <div className="info-item">
            <label>Phone:</label>
            <span>{vendor.phone}</span>
          </div>
          <div className="info-item">
            <label>Email:</label>
            <span>{vendor.email || '-'}</span>
          </div>
          <div className="info-item">
            <label>Type:</label>
            <span>{vendor.vendorType || '-'}</span>
          </div>
          <div className="info-item">
            <label>Address:</label>
            <span>{vendor.address || '-'}</span>
          </div>
          <div className="info-item">
            <label>Created:</label>
            <span>{new Date(vendor.createdAt).toLocaleDateString()}</span>
          </div>
        </div>

        <div className="outstanding-section">
          <div className="outstanding-card">
            <h3>Outstanding Amount</h3>
            <div className="outstanding-amount">{formatCurrency(outstandingAmount)}</div>
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'purchases' ? 'active' : ''}`}
            onClick={() => setActiveTab('purchases')}
          >
            Purchases / Expenses ({purchases.length})
          </button>
          <button
            className={`tab ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            Payments ({payments.length})
          </button>
          <button
            className={`tab ${activeTab === 'returns' ? 'active' : ''}`}
            onClick={() => setActiveTab('returns')}
          >
            Purchase Returns ({returns.length})
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'purchases' && (
            <div className="expenses-tab">
              {purchases.length === 0 ? (
                <div className="no-data">No purchases found for this vendor</div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Voucher No</th>
                        <th>Project</th>
                        <th>Expense Head</th>
                        <th>Amount</th>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchases.map((purchase) => (
                        <tr key={purchase.id}>
                          <td>{purchase.voucherNo || `#${purchase.id}`}</td>
                          <td>{purchase.projectName}</td>
                          <td>{purchase.category}</td>
                          <td>{formatCurrency(purchase.amount)}</td>
                          <td>{new Date(purchase.expenseDate).toLocaleDateString()}</td>
                          <td>{purchase.description || '-'}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => {
                                setSelectedItem(purchase);
                                setModalType('expense');
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="payments-tab">
              {payments.length === 0 ? (
                <div className="no-data">No payments recorded for this vendor</div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Voucher No</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Payment Date</th>
                        <th>Payment Method</th>
                        <th>Transaction Ref</th>
                        <th>Notes</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment) => {
                        const isRefund = payment.transactionType === 'INCOME';
                        return (
                          <tr key={payment.id}>
                            <td>{payment.voucherNo || `#${payment.id}`}</td>
                            <td>
                              <span style={{
                                padding: '3px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                backgroundColor: isRefund ? 'rgba(22, 163, 74, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                                color: isRefund ? '#16a34a' : '#dc3545',
                                display: 'inline-block'
                              }}>
                                {isRefund ? 'Refund' : 'Payment'}
                              </span>
                            </td>
                            <td style={{
                              fontWeight: '600',
                              color: isRefund ? '#16a34a' : '#dc3545'
                            }}>
                              {formatCurrency(payment.amount)}
                            </td>
                            <td>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                            <td>{payment.paymentMethod || '-'}</td>
                            <td>{payment.transactionRef || '-'}</td>
                            <td>{payment.notes || '-'}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                  setSelectedItem(payment);
                                  setModalType('payment');
                                }}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'returns' && (
            <div className="returns-tab">
              {returns.length === 0 ? (
                <div className="no-data">No returns recorded for this vendor</div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Voucher No</th>
                        <th>Project</th>
                        <th>Income Head</th>
                        <th>Amount</th>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {returns.map((ret) => (
                        <tr key={ret.id}>
                          <td>{ret.voucherNo || `#${ret.id}`}</td>
                          <td>{ret.projectName}</td>
                          <td>{ret.category}</td>
                          <td>{formatCurrency(ret.amount)}</td>
                          <td>{new Date(ret.expenseDate).toLocaleDateString()}</td>
                          <td>{ret.description || '-'}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => {
                                setSelectedItem(ret);
                                setModalType('expense');
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>


      {selectedItem && (
        <div className="ic-modal-overlay" onClick={() => setSelectedItem(null)}>
          <div className="ic-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
            <div className="ic-modal-header" style={{ borderBottom: '1px solid #e5e7eb', padding: '20px 24px' }}>
              <h2>
                {modalType === 'expense' ? (
                  selectedItem.transactionType === 'PURCHASE_RETURN' ? 'Purchase Return Details' : 'Purchase Details'
                ) : 'Payment Details'}
              </h2>
              <button className="ic-modal-close" onClick={() => setSelectedItem(null)}>×</button>
            </div>
            
            <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Voucher No</span>
                <span style={{ fontSize: '14px', color: '#111827', fontWeight: '600' }}>{selectedItem.voucherNo || `#${selectedItem.id}`}</span>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Amount</span>
                <span style={{ fontSize: '16px', color: selectedItem.transactionType === 'PURCHASE_RETURN' ? '#16a34a' : (modalType === 'expense' ? '#dc2626' : '#16a34a'), fontWeight: '700' }}>
                  {formatCurrency(selectedItem.amount)}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Date</span>
                <span style={{ fontSize: '14px', color: '#111827' }}>
                  {new Date(modalType === 'expense' ? selectedItem.expenseDate : selectedItem.paymentDate).toLocaleDateString()}
                </span>
              </div>

              {modalType === 'expense' ? (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Project</span>
                    <span style={{ fontSize: '14px', color: '#111827' }}>{selectedItem.projectName || '-'}</span>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>
                      {selectedItem.transactionType === 'PURCHASE_RETURN' ? 'Income Head' : 'Expense Head'}
                    </span>
                    <span style={{ fontSize: '14px', color: '#111827' }}>{selectedItem.category || '-'}</span>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Payment Method</span>
                    <span style={{ fontSize: '14px', color: '#111827' }}>{selectedItem.paymentMethod || 'CASH'}</span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Reference ID / No</span>
                    <span style={{ fontSize: '14px', color: '#111827' }}>{selectedItem.transactionRef || '-'}</span>
                  </div>
                </>
              )}
              
              <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Notes / Description</span>
                <span style={{ fontSize: '14px', color: '#4b5563', fontStyle: selectedItem.description || selectedItem.notes ? 'normal' : 'italic' }}>
                  {modalType === 'expense' ? (selectedItem.description || 'No description provided') : (selectedItem.notes || 'No notes provided')}
                </span>
              </div>

              {selectedItem.transactionType === 'PURCHASE_RETURN' && assocRefund && (
                <div style={{ gridColumn: 'span 2', borderTop: '1px solid #e5e7eb', paddingTop: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <h3 style={{ gridColumn: 'span 2', fontSize: '12px', margin: '0 0 4px 0', fontWeight: '600', textTransform: 'uppercase', color: 'var(--accent, #3b82f6)' }}>Return Collection Details</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Collected Amount</span>
                    <span style={{ fontSize: '14px', color: '#16a34a', fontWeight: '600' }}>{formatCurrency(assocRefund.amount)}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Refund Account</span>
                    <span style={{ fontSize: '14px', color: '#111827' }}>{assocRefund.category}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Refund Mode</span>
                    <span style={{ fontSize: '14px', color: '#111827' }}>{assocRefund.paymentMethod}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Reference ID / No</span>
                    <span style={{ fontSize: '14px', color: '#111827' }}>{assocRefund.transactionRef}</span>
                  </div>
                </div>
              )}

              {selectedItem.items && selectedItem.items.length > 0 && (
                <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Items ({selectedItem.items.length})</span>
                  <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                    <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                          <th style={{ padding: '8px 12px', fontWeight: '600' }}>Item Code & Name</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600', textAlign: 'right' }}>Qty</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600', textAlign: 'right' }}>Price</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600', textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedItem.items.map(item => (
                          <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '8px 12px' }}>
                              <div style={{ fontWeight: '500' }}>{item.inventoryItemName || item.itemName}</div>
                              <div style={{ fontSize: '11px', color: '#6b7280' }}>{item.inventoryItemCode || item.itemCode}</div>
                              {item.notes && <div style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>Notes: {item.notes}</div>}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right' }}>{parseFloat(item.quantity).toFixed(2)}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatCurrency(item.unitPrice)}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '500' }}>{formatCurrency(item.totalAmount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            
            <div className="ic-modal-actions" style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <button type="button" className="ic-btn-secondary" onClick={() => setSelectedItem(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VendorDetail;
